import React, { FC, useMemo } from 'react'
import { Modal, Tabs, Badge, Button } from 'antd'
import { useLocalization } from 'localization'
import { useHideModal } from 'components/Modals'
import { SETTINGS_TABS } from './consts'
import PersonalTab from '../PersonalTab'
import AccessTab from '../AccessTab'
import TaxesTab from '../TaxesTab'
import BillingTab from '../BillingTab'
import { useCurrentUser, useReceiverInvites } from 'api'
import { useOrganizationById } from 'api/organizations/queries'
import { useIsWealthicaUser } from 'modules/common/hooks'

export interface ISettingsModal {
	initialTab?: keyof typeof SETTINGS_TABS
}

const SettingsModal: FC<ISettingsModal> = ({ initialTab = SETTINGS_TABS.PERSONAL }) => {
	const { t } = useLocalization()
	const hideModal = useHideModal()
	const { data: user, isFetched: isUserFetched, cpaClientView, isCPAview } = useCurrentUser()
	const { data: organization } = useOrganizationById(user?.orgId)
	const onClose = () => {
		hideModal()
	}
	const { data: receiverInvites = [] } = useReceiverInvites('ACCESS', {
		enabled: !user?.isCPA && isUserFetched,
	})
	const isWealthicaUser = useIsWealthicaUser()

	const tabKeys = useMemo(
		() => ({
			[SETTINGS_TABS.PERSONAL]: t('settings.tabs.personal'),
			[SETTINGS_TABS.TAXES]: t('settings.tabs.taxes'),
			[SETTINGS_TABS.BILLING]: t('settings.tabs.billing'),
			[SETTINGS_TABS.ACCESS]: t('settings.tabs.access'),
		}),
		[t]
	)

	return (
		<Modal title={t('terms.settings')} width={660} open onCancel={onClose} footer={null}>
			<Tabs
				defaultActiveKey={tabKeys[initialTab]}
				data-testid="settings-tabs"
				size="small"
				destroyInactiveTabPane
				tabBarExtraContent={
					<Button
						size="small"
						type="link"
						onClick={() => {
							document.querySelector('.cky-btn-revisit-wrapper')?.dispatchEvent(new Event('click'))
							hideModal()
						}}
					>
						{t('terms.cookiePolicy')}
					</Button>
				}
			>
				<Tabs.TabPane tab={tabKeys.PERSONAL} key={tabKeys.PERSONAL}>
					<PersonalTab />
				</Tabs.TabPane>
				<Tabs.TabPane tab={tabKeys.TAXES} key={tabKeys.TAXES}>
					<TaxesTab />
				</Tabs.TabPane>
				{((!user?.isCPA && !cpaClientView) || (organization?.isAdmin && isCPAview)) && !isWealthicaUser && (
					<Tabs.TabPane tab={tabKeys.BILLING} key={tabKeys.BILLING}>
						<BillingTab />
					</Tabs.TabPane>
				)}
				{!user?.isCPA && !cpaClientView && isUserFetched && (
					<Tabs.TabPane
						tab={
							receiverInvites.length ? (
								<Badge size="small" count={receiverInvites.length}>
									{tabKeys.ACCESS}
								</Badge>
							) : (
								tabKeys.ACCESS
							)
						}
						key={tabKeys.ACCESS}
					>
						<AccessTab />
					</Tabs.TabPane>
				)}
			</Tabs>
		</Modal>
	)
}

export default SettingsModal
